// MolesCalculator.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Jumlah Mol - Hitung Mol Kimia dengan Akurasi Tinggi";
const DESCRIPTION =
  "Kalkulator mol kimia gratis - hitung jumlah mol dari massa dan massa molar, atau dari jumlah partikel, atau dari volume gas. Hasil instan dan akurat untuk siswa dan guru.";
const KEYWORDS =
  "hitung jumlah mol, kalkulator mol, rumus jumlah mol, massa molar, mol kimia, bilangan Avogadro, volume molar, kalkulator kimia, moles calculator, molar mass";
const OG_URL = "https://do-calculate.com/calculator/id/moles-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Jumlah Mol Kimia",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Jumlah Mol",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung jumlah mol dari massa?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Untuk menghitung jumlah mol dari massa, gunakan rumus: jumlah mol = massa (gram) ÷ massa molar (gram/mol). Contoh: 36 gram air H₂O menghasilkan 36 ÷ 18 = 2 mol.",
        },
      },
      {
        "@type": "Question",
        name: "Apa itu bilangan Avogadro?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Bilangan Avogadro adalah 6,022 × 10²³ partikel/mol. Ini mewakili jumlah partikel (atom, molekul, atau ion) dalam satu mol zat apa pun.",
        },
      },
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung jumlah mol dari volume gas?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Pada suhu dan tekanan standar (STP), satu mol gas ideal menempati volume 22,4 liter. Rumus: jumlah mol = volume (liter) ÷ 22,4. Contoh: 44,8 liter oksigen = 44,8 ÷ 22,4 = 2 mol.",
        },
      },
    ],
  },
];

const AVOGADRO = 6.022e23;
const MOLAR_VOLUME_STP = 22.4;

const CALC_METHODS = {
  mass_to_moles: "Dari massa dan massa molar",
  particles_to_moles: "Dari jumlah partikel",
  volume_to_moles: "Dari volume gas (STP)",
  moles_to_mass: "Dari mol ke massa",
  moles_to_particles: "Dari mol ke jumlah partikel",
  moles_to_volume: "Dari mol ke volume gas (STP)",
};

const COMMON_SUBSTANCES = {
  custom: { name: "Zat Kustom", molarMass: 0 },
  H2O: { name: "Air H₂O", molarMass: 18.015 },
  NaCl: { name: "Natrium Klorida NaCl", molarMass: 58.44 },
  CO2: { name: "Karbon Dioksida CO₂", molarMass: 44.01 },
  O2: { name: "Oksigen O₂", molarMass: 32.0 },
  H2: { name: "Hidrogen H₂", molarMass: 2.016 },
  N2: { name: "Nitrogen N₂", molarMass: 28.014 },
  CH4: { name: "Metana CH₄", molarMass: 16.043 },
  H2SO4: { name: "Asam Sulfat H₂SO₄", molarMass: 98.079 },
  HCl: { name: "Asam Klorida HCl", molarMass: 36.461 },
  NaOH: { name: "Natrium Hidroksida NaOH", molarMass: 40.0 },
  CaCO3: { name: "Kalsium Karbonat CaCO₃", molarMass: 100.087 },
  C6H12O6: { name: "Glukosa C₆H₁₂O₆", molarMass: 180.156 },
  Fe: { name: "Besi Fe", molarMass: 55.845 },
  Cu: { name: "Tembaga Cu", molarMass: 63.546 },
  Al: { name: "Aluminium Al", molarMass: 26.982 },
  Ca: { name: "Kalsium Ca", molarMass: 40.078 },
  Mg: { name: "Magnesium Mg", molarMass: 24.305 },
  C: { name: "Karbon C", molarMass: 12.011 },
  Na: { name: "Natrium Na", molarMass: 22.99 },
};

function MolesCalculator() {
  const [calcMethod, setCalcMethod] = useState("mass_to_moles");
  const [substance, setSubstance] = useState("H2O");
  const [customMolarMass, setCustomMolarMass] = useState("");

  const [mass, setMass] = useState("");
  const [moles, setMoles] = useState("");
  const [particles, setParticles] = useState("");
  const [volume, setVolume] = useState("");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const getMolarMass = () => {
    if (substance === "custom") return parseFloat(customMolarMass || 0);
    return COMMON_SUBSTANCES[substance]?.molarMass || 0;
  };

  const formatScientific = (num) => {
    if (num === 0) return "0";
    if (Math.abs(num) >= 1e6 || Math.abs(num) < 0.0001) {
      return num.toExponential(4);
    }
    return num.toLocaleString("en-US", { maximumFractionDigits: 6 });
  };

  const calculateResult = () => {
    try {
      setError("");
      const molarMass = getMolarMass();
      let resultData = null;

      switch (calcMethod) {
        case "mass_to_moles": {
          const massVal = parseFloat(mass);
          if (!mass || isNaN(massVal) || massVal <= 0) {
            setError("Harap masukkan massa yang valid dan positif");
            setResult(null);
            return;
          }
          if (molarMass <= 0) {
            setError("Harap masukkan massa molar yang valid");
            setResult(null);
            return;
          }
          const molesResult = massVal / molarMass;
          const particlesResult = molesResult * AVOGADRO;
          const volumeResult = molesResult * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesResult,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massVal,
            molarMass,
            formula: `Jumlah mol = ${massVal} g ÷ ${molarMass} g/mol = ${molesResult.toFixed(6)} mol`,
          };
          break;
        }

        case "particles_to_moles": {
          const particlesVal = parseFloat(particles);
          if (!particles || isNaN(particlesVal) || particlesVal <= 0) {
            setError("Harap masukkan jumlah partikel yang valid dan positif");
            setResult(null);
            return;
          }
          const molesResult = particlesVal / AVOGADRO;
          const massResult = molarMass > 0 ? molesResult * molarMass : null;
          const volumeResult = molesResult * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesResult,
            particles: particlesVal,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `Jumlah mol = ${particlesVal.toExponential(3)} ÷ 6,022×10²³ = ${molesResult.toExponential(4)} mol`,
          };
          break;
        }

        case "volume_to_moles": {
          const volumeVal = parseFloat(volume);
          if (!volume || isNaN(volumeVal) || volumeVal <= 0) {
            setError("Harap masukkan volume yang valid dan positif");
            setResult(null);
            return;
          }
          const molesResult = volumeVal / MOLAR_VOLUME_STP;
          const particlesResult = molesResult * AVOGADRO;
          const massResult = molarMass > 0 ? molesResult * molarMass : null;
          resultData = {
            moles: molesResult,
            particles: particlesResult,
            volumeSTP: volumeVal,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `Jumlah mol = ${volumeVal} L ÷ 22,4 L/mol = ${molesResult.toFixed(6)} mol`,
          };
          break;
        }

        case "moles_to_mass": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("Harap masukkan jumlah mol yang valid dan positif");
            setResult(null);
            return;
          }
          if (molarMass <= 0) {
            setError("Harap masukkan massa molar yang valid");
            setResult(null);
            return;
          }
          const massResult = molesVal * molarMass;
          const particlesResult = molesVal * AVOGADRO;
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass,
            formula: `Massa = ${molesVal} mol × ${molarMass} g/mol = ${massResult.toFixed(4)} g`,
          };
          break;
        }

        case "moles_to_particles": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("Harap masukkan jumlah mol yang valid dan positif");
            setResult(null);
            return;
          }
          const particlesResult = molesVal * AVOGADRO;
          const massResult = molarMass > 0 ? molesVal * molarMass : null;
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `Jumlah partikel = ${molesVal} × 6,022×10²³ = ${particlesResult.toExponential(4)}`,
          };
          break;
        }

        case "moles_to_volume": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) {
            setError("Harap masukkan jumlah mol yang valid dan positif");
            setResult(null);
            return;
          }
          const volumeResult = molesVal * MOLAR_VOLUME_STP;
          const particlesResult = molesVal * AVOGADRO;
          const massResult = molarMass > 0 ? molesVal * molarMass : null;
          resultData = {
            moles: molesVal,
            particles: particlesResult,
            volumeSTP: volumeResult,
            mass: massResult,
            molarMass: molarMass > 0 ? molarMass : null,
            formula: `Volume pada STP = ${molesVal} mol × 22,4 L/mol = ${volumeResult.toFixed(4)} L`,
          };
          break;
        }

        default:
          throw new Error("Metode perhitungan tidak dikenal");
      }

      resultData.substanceName =
        substance === "custom"
          ? "Zat Kustom"
          : COMMON_SUBSTANCES[substance]?.name || "";
      resultData.methodName = CALC_METHODS[calcMethod];
      setResult(resultData);
    } catch (err) {
      setError("Kesalahan dalam perhitungan: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    const hasInput = mass || moles || particles || volume;
    if (hasInput) {
      calculateResult();
    }
  }, [calcMethod, substance, customMolarMass, mass, moles, particles, volume]);

  const handleClear = () => {
    setMass("");
    setMoles("");
    setParticles("");
    setVolume("");
    setCustomMolarMass("");
    setResult(null);
    setError("");
  };

  const renderInputFields = () => {
    switch (calcMethod) {
      case "mass_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label >Massa (gram)</label>
              <input
                type="number"
                className="form-control"
                value={mass}
                onChange={(e) => setMass(e.target.value)}
                min="0"
                step="0.001"
                placeholder="Masukkan massa dalam gram"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <label >Massa Molar (g/mol)</label>
              <input
                type="number"
                className="form-control"
                value={getMolarMass() || ""}
                readOnly={substance !== "custom"}
                onChange={(e) =>
                  substance === "custom" && setCustomMolarMass(e.target.value)
                }
                min="0"
                step="0.001"
                placeholder="Massa molar"
                style={
                  substance !== "custom"
                    ? { backgroundColor: "#f0f9f4" }
                    : { backgroundColor: "#fff3cd" }
                }
              />
            </div>
          </div>
        );

      case "particles_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >
                Jumlah Partikel (atom / molekul / ion)
              </label>
              <input
                type="number"
                className="form-control"
                value={particles}
                onChange={(e) => setParticles(e.target.value)}
                min="0"
                step="any"
                placeholder="Masukkan jumlah partikel (contoh: 3,011e23)"
              />
              <small className="text-muted">
                Bilangan Avogadro = 6,022 × 10²³ partikel/mol
              </small>
            </div>
          </div>
        );

      case "volume_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >Volume Gas pada STP (Liter)</label>
              <input
                type="number"
                className="form-control"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                min="0"
                step="0.001"
                placeholder="Masukkan volume dalam liter"
              />
              <small className="text-muted">
                Volume molar pada STP = 22,4 L/mol (0°C, 1 atm)
              </small>
            </div>
          </div>
        );

      case "moles_to_mass":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label >Jumlah Mol (mol)</label>
              <input
                type="number"
                className="form-control"
                value={moles}
                onChange={(e) => setMoles(e.target.value)}
                min="0"
                step="0.0001"
                placeholder="Masukkan jumlah mol"
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <label >Massa Molar (g/mol)</label>
              <input
                type="number"
                className="form-control"
                value={getMolarMass() || ""}
                readOnly={substance !== "custom"}
                onChange={(e) =>
                  substance === "custom" && setCustomMolarMass(e.target.value)
                }
                min="0"
                step="0.001"
                placeholder="Massa molar"
                style={
                  substance !== "custom"
                    ? { backgroundColor: "#f0f9f4" }
                    : { backgroundColor: "#fff3cd" }
                }
              />
            </div>
          </div>
        );

      case "moles_to_particles":
      case "moles_to_volume":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label >Jumlah Mol (mol)</label>
              <input
                type="number"
                className="form-control"
                value={moles}
                onChange={(e) => setMoles(e.target.value)}
                min="0"
                step="0.0001"
                placeholder="Masukkan jumlah mol"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const needsMolarMass = [
    "mass_to_moles",
    "moles_to_mass",
    "particles_to_moles",
    "volume_to_moles",
    "moles_to_particles",
    "moles_to_volume",
  ].includes(calcMethod);

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>
              Kalkulator Jumlah Mol Kimia
            </h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Jumlah Mol</strong> adalah alat kimia gratis dan akurat yang membantu Anda <strong>menghitung jumlah mol</strong> dengan tiga cara berbeda: dari massa dan massa molar, atau dari jumlah partikel menggunakan bilangan Avogadro, atau dari volume gas pada kondisi standar. Ideal untuk siswa kimia, guru, dan laboratorium sains.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Hitung Jumlah Mol dengan Mudah
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="calcMethod" >
                    Metode Perhitungan
                  </label>
                  <select
                    id="calcMethod"
                    className="form-select"
                    value={calcMethod}
                    onChange={(e) => {
                      setCalcMethod(e.target.value);
                      handleClear();
                    }}
                  >
                    {Object.entries(CALC_METHODS).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {needsMolarMass && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="substance" >
                      Zat Kimia
                    </label>
                    <select
                      id="substance"
                      className="form-select"
                      value={substance}
                      onChange={(e) => setSubstance(e.target.value)}
                    >
                      {Object.entries(COMMON_SUBSTANCES).map(
                        ([key, { name, molarMass }]) => (
                          <option key={key} value={key}>
                            {name}{" "}
                            {molarMass > 0
                              ? `(${molarMass} g/mol)`
                              : "(Masukkan massa molar)"}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              )}

              {substance === "custom" &&
                [
                  "mass_to_moles",
                  "moles_to_mass",
                  "particles_to_moles",
                  "volume_to_moles",
                  "moles_to_particles",
                  "moles_to_volume",
                ].includes(calcMethod) && (
                  <div className="row">
                    <div className="col-sm-12">
                      <label htmlFor="customMolarMass" >
                        Massa Molar Kustom (g/mol)
                      </label>
                      <input
                        id="customMolarMass"
                        type="number"
                        className="form-control"
                        value={customMolarMass}
                        onChange={(e) => setCustomMolarMass(e.target.value)}
                        min="0"
                        step="0.001"
                        placeholder="Masukkan massa molar zat"
                        style={{ backgroundColor: "#fff3cd" }}
                      />
                    </div>
                  </div>
                )}

              {renderInputFields()}

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  Hapus
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>Hasil:</strong>
                  </h3>

                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            Besaran
                          </th>
                          <th
                            className="bmi-header"
                            style={{ textAlign: "center" }}
                          >
                            Nilai
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah Mol</td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {formatScientific(result.moles)} mol
                           </td>
                         </tr>
                        {result.mass !== null && result.mass !== undefined && (
                          <tr>
                            <td style={{ textAlign: "center" }}>Massa</td>
                            <td
                              style={{
                                textAlign: "center",
                                fontSize: "16px",
                                fontWeight: "bold",
                                color: "#0f3d28",
                              }}
                            >
                              {formatScientific(result.mass)} gram
                             </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ textAlign: "center" }}>Jumlah Partikel</td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>
                            {result.particles.toExponential(4)} partikel
                           </td>
                         </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            Volume pada STP
                           </td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>
                            {formatScientific(result.volumeSTP)} liter
                           </td>
                         </tr>
                      </tbody>
                     </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Hasil Tambahan</strong>
                      </div>
                      <div className="info-section-body">
                        <ul
                          style={{
                            listStyle: "none",
                            padding: 0,
                            lineHeight: "2",
                          }}
                        >
                          <li>
                            🧪 <strong>Jumlah Mol:</strong>{" "}
                            {formatScientific(result.moles)} mol
                          </li>
                          {result.mass !== null && result.mass !== undefined && (
                            <li>
                              ⚖️ <strong>Massa:</strong>{" "}
                              {formatScientific(result.mass)} gram
                            </li>
                          )}
                          <li>
                            ⚛️ <strong>Jumlah Partikel:</strong>{" "}
                            {result.particles.toExponential(4)}
                          </li>
                          <li>
                            🫧 <strong>Volume pada STP:</strong>{" "}
                            {formatScientific(result.volumeSTP)} liter
                          </li>
                          {result.molarMass && (
                            <li>
                              📋 <strong>Massa Molar:</strong>{" "}
                              {result.molarMass} g/mol
                            </li>
                          )}
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 Zat:</strong> {result.substanceName}
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Perhitungan</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>Metode Perhitungan:</strong> {result.methodName}
                          </p>
                          <p>
                            <strong>Zat:</strong> {result.substanceName}
                          </p>
                          {result.molarMass && (
                            <p>
                              <strong>Massa Molar:</strong>{" "}
                              {result.molarMass} g/mol
                            </p>
                          )}
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}>
                            <strong>Langkah Perhitungan:</strong>
                          </p>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              backgroundColor: "#f8f9fa",
                              padding: "10px",
                              borderRadius: "5px",
                            }}
                          >
                            {result.formula}
                          </p>
                          <p style={{ fontSize: "13px", color: "#666" }}>
                            Bilangan Avogadro = 6,022 × 10²³ partikel/mol
                            <br />
                            Volume molar pada STP = 22,4 L/mol
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Kalkulator Jumlah Mol?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator Jumlah Mol</strong> adalah alat elektronik gratis yang mengkonversi antara massa, jumlah mol, jumlah partikel, dan volume gas dalam sekejap. Mol adalah satuan pengukuran dasar dalam kimia; mewakili jumlah partikel tertentu yang sama dengan <strong>6,022 × 10²³ partikel</strong>, yang dikenal sebagai Bilangan Avogadro. Gunakan kalkulator ini untuk:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menghitung mol dari massa:</strong> Bagi massa zat dalam gram dengan massa molarnya
                  </li>
                  <li>
                    <strong>Konversi dari partikel:</strong> Bagi jumlah partikel dengan bilangan Avogadro
                  </li>
                  <li>
                    <strong>Menghitung mol gas:</strong> Bagi volume dalam liter dengan 22,4 L/mol pada STP
                  </li>
                  <li>
                    <strong>Sebaliknya:</strong> Konversi mol menjadi massa, partikel, atau volume gas
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Cara Menggunakan Kalkulator Langkah demi Langkah:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih metode perhitungan yang sesuai dari daftar dropdown</li>
                  <li>
                    Pilih zat kimia (atau "Zat Kustom" untuk memasukkan massa molar secara manual)
                  </li>
                  <li>Masukkan nilai yang diketahui (massa, mol, partikel, atau volume)</li>
                  <li>Hasil muncul seketika dengan semua konversi terkait</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 Catatan:</strong> Kalkulator memberi Anda jumlah mol, massa, jumlah partikel, dan volume pada STP sekaligus — hemat waktu Anda dan hindari kesalahan perhitungan manual.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Penggunaan Kalkulator Mol</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Dalam Studi dan Ujian:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Memecahkan masalah kimia:</strong> Hitung mol dengan cepat dalam soal persamaan kimia
                  </li>
                  <li>
                    <strong>Review ujian:</strong> Periksa jawaban Anda sebelum mengumpulkan lembar jawaban
                  </li>
                  <li>
                    <strong>Memahami hubungan kimia:</strong> Pahami hubungan antara massa, mol, dan partikel
                  </li>
                  <li>
                    <strong>Menyelesaikan latihan bertahap:</strong> Berlatih dengan berbagai jenis soal
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Di Laboratorium Sains:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menyiapkan larutan:</strong> Tentukan jumlah zat yang diperlukan secara akurat
                  </li>
                  <li>
                    <strong>Perhitungan reaksi:</strong> Tentukan jumlah reaktan dan produk
                  </li>
                  <li>
                    <strong>Pengukuran gas:</strong> Konversi volume gas menjadi mol dengan mudah
                  </li>
                  <li>
                    <strong>Dokumentasi eksperimen:</strong> Catat hasil dalam satuan standar yang akurat
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Industri Kimia:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Kontrol rasio reaksi:</strong> Hitung jumlah bahan baku secara akurat
                  </li>
                  <li>
                    <strong>Kontrol kualitas:</strong> Verifikasi konsentrasi produk kimia
                  </li>
                  <li>
                    <strong>Peningkatan efisiensi:</strong> Kurangi pemborosan melalui perhitungan yang akurat
                  </li>
                  <li>
                    <strong>Resep industri:</strong> Sesuaikan proporsi bahan dalam manufaktur
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Pengajaran dan Pendidikan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menjelaskan konsep:</strong> Tampilkan konversi secara visual dan jelas
                  </li>
                  <li>
                    <strong>Verifikasi instan:</strong> Uji jawaban siswa pada soal
                  </li>
                  <li>
                    <strong>Desain aktivitas:</strong> Hasilkan berbagai contoh untuk latihan
                  </li>
                  <li>
                    <strong>Persiapan ujian:</strong> Pastikan kebenaran soal ujian
                  </li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Rumus Menghitung Jumlah Mol</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Mol terkait dengan tiga besaran dasar: massa, jumlah partikel, dan volume gas. Setiap rumus menggambarkan hubungan spesifik antara besaran-besaran ini:
                </p>
                <table
                  className="table bmi-table"
                  style={{ marginTop: "1rem" }}
                >
                  <thead>
                    <tr>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Rumus
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Persamaan
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Variabel
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Mol dari Massa</td>
                      <td style={{ textAlign: "center" }}>
                        n = m ÷ M
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: mol, m: massa (g), M: massa molar (g/mol)
                      </td>
                     </tr>
                     <tr>
                      <td style={{ textAlign: "center" }}>
                        Mol dari Partikel
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n = N ÷ Nₐ
                      </td>
                      <td style={{ textAlign: "center" }}>
                        N: jumlah partikel, Nₐ = 6,022×10²³
                      </td>
                     </tr>
                     <tr>
                      <td style={{ textAlign: "center" }}>Mol dari Volume</td>
                      <td style={{ textAlign: "center" }}>
                        n = V ÷ 22,4
                      </td>
                      <td style={{ textAlign: "center" }}>
                        V: volume dalam liter pada STP (0°C, 1 atm)
                      </td>
                     </tr>
                     <tr>
                      <td style={{ textAlign: "center" }}>
                        Massa dari Mol
                      </td>
                      <td style={{ textAlign: "center" }}>
                        m = n × M
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: mol, M: massa molar (g/mol)
                      </td>
                     </tr>
                     <tr>
                      <td style={{ textAlign: "center" }}>
                        Partikel dari Mol
                      </td>
                      <td style={{ textAlign: "center" }}>
                        N = n × Nₐ
                      </td>
                      <td style={{ textAlign: "center" }}>
                        n: mol, Nₐ = 6,022×10²³
                      </td>
                     </tr>
                     <tr>
                      <td style={{ textAlign: "center" }}>Volume dari Mol</td>
                      <td style={{ textAlign: "center" }}>
                        V = n × 22,4
                       </td>
                      <td style={{ textAlign: "center" }}>
                        n: mol, volume dalam liter pada STP
                       </td>
                     </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Apa itu Mol Secara Spesifik?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Mol bukan sekadar angka — ini adalah satuan pengukuran kuantitas seperti halnya lusin berarti 12. Setiap mol zat apa pun mengandung <strong>6,022 × 10²³</strong> partikelnya. Angka besar ini tidak sembarangan: ini adalah jumlah atom yang ditemukan tepat dalam 12 gram karbon-12. Para ilmuwan memilih definisi ini untuk menghubungkan kimia mikroskopis (atom) dengan pengukuran laboratorium (gram dan mililiter).
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Massa Molar Beberapa Zat Umum:
                </h3>
                <table
                  className="table bmi-table"
                  style={{ marginTop: "0.5rem" }}
                >
                  <thead>
                    <tr>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Zat
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Simbol
                      </th>
                      <th
                        className="bmi-header"
                        style={{ textAlign: "center" }}
                      >
                        Massa Molar (g/mol)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Air</td>
                      <td style={{ textAlign: "center" }}>H₂O</td>
                      <td style={{ textAlign: "center" }}>18,015</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        Karbon Dioksida
                      </td>
                      <td style={{ textAlign: "center" }}>CO₂</td>
                      <td style={{ textAlign: "center" }}>44,01</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        Natrium Klorida (Garam)
                      </td>
                      <td style={{ textAlign: "center" }}>NaCl</td>
                      <td style={{ textAlign: "center" }}>58,44</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>
                        Asam Sulfat
                      </td>
                      <td style={{ textAlign: "center" }}>H₂SO₄</td>
                      <td style={{ textAlign: "center" }}>98,079</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Glukosa</td>
                      <td style={{ textAlign: "center" }}>C₆H₁₂O₆</td>
                      <td style={{ textAlign: "center" }}>180,156</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Oksigen</td>
                      <td style={{ textAlign: "center" }}>O₂</td>
                      <td style={{ textAlign: "center" }}>32,0</td>
                    </tr>
                  </tbody>
                </table>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ Bagaimana cara menghitung massa molar?</strong> Jumlahkan massa atom semua unsur dalam molekul. Contoh: air H₂O = (2 × 1,008) + 16,0 = 18,016 g/mol.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Menghitung Jumlah Mol</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Contoh 1: Berapa mol dalam 36 gram air?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Diketahui:</strong> Massa air = 36 g, massa molar H₂O = 18,015 g/mol
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Penyelesaian:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Dari massa dan massa molar"</li>
                  <li>Pilih "Air H₂O" dari daftar zat</li>
                  <li>Masukkan massa: 36 gram</li>
                  <li>Hasil: 36 ÷ 18,015 ≈ 1,999 mol</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Verifikasi:</strong> 2 mol × 18,015 = 36,03 g ✓
                </div>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 2: Berapa mol dalam 3,011 × 10²³ atom?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Diketahui:</strong> Jumlah partikel = 3,011 × 10²³ atom
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Penyelesaian:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Dari jumlah partikel"</li>
                  <li>Masukkan: 3,011e23</li>
                  <li>
                    Hasil: 3,011×10²³ ÷ 6,022×10²³ = <strong>0,5 mol</strong>
                  </li>
                </ol>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 3: Berapa mol dalam 44,8 liter karbon dioksida?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Diketahui:</strong> Volume CO₂ = 44,8 liter pada STP
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Penyelesaian:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Dari volume gas (STP)"</li>
                  <li>Masukkan volume: 44,8 liter</li>
                  <li>
                    Hasil: 44,8 ÷ 22,4 = <strong>2 mol</strong>
                  </li>
                  <li>Massa yang sesuai: 2 × 44,01 = 88,02 g</li>
                </ol>

                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 4: Berapa gram yang setara dengan 0,5 mol garam dapur?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Diketahui:</strong> 0,5 mol NaCl, massa molarnya = 58,44 g/mol
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Penyelesaian:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Dari mol ke massa"</li>
                  <li>Pilih "Natrium Klorida NaCl"</li>
                  <li>Masukkan: 0,5 mol</li>
                  <li>
                    Hasil: 0,5 × 58,44 = <strong>29,22 gram</strong>
                  </li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Menghitung Mol dengan Benar</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Tips Umum:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Pelajari massa atom:</strong> Hafalkan massa unsur umum (H=1, C=12, N=14, O=16, Na=23, Cl=35,5) untuk menghitung massa molar dengan cepat
                  </li>
                  <li>
                    <strong>Periksa satuan:</strong> Massa harus dalam gram, volume dalam liter pada STP
                  </li>
                  <li>
                    <strong>Gunakan notasi ilmiah:</strong> Untuk jumlah partikel yang besar, tulis misalnya 6,022e23
                  </li>
                  <li>
                    <strong>Periksa hasil Anda:</strong> Kalikan mol dengan massa molar, Anda akan mendapatkan massa aslinya
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Kesalahan Umum yang Harus Dihindari:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    Mencampuradukkan massa molar unsur dengan molekul — ingat O₂ massanya 32 bukan 16
                  </li>
                  <li>
                    Menggunakan hukum volume molar untuk non-gas — tidak berlaku untuk cairan dan padatan
                  </li>
                  <li>
                    Lupa bahwa rumus V = n × 22,4 hanya berlaku pada kondisi standar (STP)
                  </li>
                  <li>
                    Membagi mol dengan massa molar, bukan mengalikannya saat menghitung massa
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk Pengguna Lanjutan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    Masukkan massa molar kustom untuk paduan atau senyawa yang tidak terdaftar
                  </li>
                  <li>
                    Gunakan hasil untuk menghitung rasio reaktan dan produk dalam persamaan kimia
                  </li>
                  <li>
                    Konversi mol larutan menggunakan rumus konsentrasi: c = n ÷ V(L)
                  </li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ Peringatan:</strong> Massa molar yang benar adalah dasar dari semua perhitungan — kesalahan kecil di dalamnya akan menghasilkan hasil yang sepenuhnya salah. Periksa tabel periodik atau gunakan daftar zat dalam kalkulator.
                </div>
              </div>
            </div>
          </div>

          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            Pertanyaan Umum
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apa itu mol dalam kimia?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Mol adalah satuan dasar untuk jumlah zat dalam kimia. Satu mol setara dengan jumlah <strong>6,022 × 10²³ partikel</strong> (atom, molekul, atau ion), dan angka ini dikenal sebagai Bilangan Avogadro. Sederhananya, seperti lusin berarti 12, mol berarti 6,022 × 10²³ — hanya pada skala atom yang sangat besar yang memungkinkan kimiawan "menghitung" atom yang tidak terlihat.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana cara menghitung jumlah mol dari massa?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Rumusnya langsung: <strong>Jumlah mol = massa ÷ massa molar</strong>.
                  Masukkan massa zat dalam gram, lalu bagi dengan massa molarnya (dalam g/mol). Contoh: Anda memiliki 58,44 gram garam dapur NaCl dan massa molarnya 58,44 g/mol, maka jumlah mol = 58,44 ÷ 58,44 = tepat 1 mol.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apa perbedaan antara massa atom dan massa molar?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Massa atom adalah massa satu atom yang diukur dalam satuan massa atom (sma atau Da), sedangkan massa molar adalah massa satu mol penuh (yaitu 6,022 × 10²³ atom) yang diukur dalam gram/mol. Angkanya sama! Karbon memiliki massa atom 12,011 sma dan massa molar 12,011 g/mol. Kesesuaian yang cerdik ini yang membuat konsep mol praktis di laboratorium.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Kapan hukum volume molar 22,4 liter diterapkan?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Hukum volume molar 22,4 L/mol hanya berlaku untuk gas ideal, dan secara spesifik pada kondisi standar: <strong>suhu 0°C dan tekanan 1 atm (STP)</strong>.
                  Pada suhu atau tekanan yang berbeda, hukum gas ideal PV = nRT digunakan sebagai gantinya. Jangan terapkan hukum ini pada cairan atau padatan karena hanya berlaku untuk gas.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana cara mencari massa molar senyawa kimia?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Jumlahkan massa atom semua atom dalam senyawa dari tabel periodik. Misalnya untuk menghitung massa molar natrium sulfat Na₂SO₄: (2 × 23) + 32 + (4 × 16) = 46 + 32 + 64 = <strong>142 g/mol</strong>.
                  Jika Anda tidak menemukan zat dalam daftar kalkulator, pilih "Zat Kustom" dan masukkan massa molar secara manual.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bisakah jumlah mol menjadi pecahan?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Tentu saja! Jumlah mol dapat berupa bilangan positif apa pun, baik bilangan bulat, pecahan, atau desimal. Misalnya, 0,5 mol air berarti 9 gram, dan 0,25 mol NaCl berarti 14,61 gram. Dalam praktik laboratorium, Anda sering berurusan dengan pecahan mol karena jumlah yang diukur belum tentu merupakan kelipatan bilangan bulat.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apa hubungan antara mol dan reaksi kimia?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Koefisien dalam persamaan kimia mewakili rasio molar reaktan dan produk. Misalnya dalam: 2H₂ + O₂ → 2H₂O, 2 mol hidrogen bereaksi dengan 1 mol oksigen untuk menghasilkan 2 mol air. Rasio ini memungkinkan Anda menghitung "reaktan pembatas" dan jumlah produk dalam reaksi apa pun — ini adalah inti dari stoikiometri.
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apakah kalkulator ini cocok untuk siswa SMA dan universitas?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Sangat cocok, kalkulator dirancang untuk semua tingkat. Siswa SMA mendapat manfaat dari pemecahan soal dasar "jumlah mol", sementara mahasiswa mendapat manfaat dari konversi cepat antara berbagai besaran dalam kimia analitik dan organik. Dosen kimia juga dapat menggunakannya untuk memeriksa soal ujian dan latihan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MolesCalculator;